import './index.css'
import cn from 'clsx'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import orderBy from 'lodash/orderBy'
import uniq from 'lodash/uniq'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { State } from 'types/redux'
import type {
  NativeCategory,
  NativeTag,
  NativeTagWithCategory,
} from 'types/track'
import { createCustomNativeTag } from '@disco/state-management/actions/CustomNativeTagActions'
import { applyNativeTagsToTracks } from '@disco/state-management/actions/PlaylistTrackActions'
import mixpanel from '@disco/api/mixpanel'
import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getNativeCategories,
  getNativeTags,
} from '@disco/state-management/selectors/tags'
import { COLORS } from '@disco/utils/color-indicators'
import { getPageXOffset, getPageYOffset } from '@disco/utils/dom'
import { keyEqualTo } from '@disco/utils/keyboard'
import { matchTag, tagScore } from '@disco/utils/tagSearch'
import ColorIndicatorsAlert from 'components/common/color-indicators-alert'
import { Box } from 'components/ds'
import Button from 'components/ui/button'
import DiscoModal from 'components/ui/disco-modal'
import Heading from 'components/ui/heading'
import Icon from 'components/ui/icon'
import Popover from 'components/ui/popover'
import { RadioButton } from 'components/ui/radio'
import Tag from 'components/ui/tag'
import TagsInput from 'components/ui/tags-input'
import TextOverflow from 'components/ui/text-overflow'
import useEventListener from 'hooks/use-event-listener'
import useLocalStorage from 'hooks/use-local-storage'
let appNode

if (typeof document !== 'undefined') {
  appNode = document.getElementById('app') || document.getElementById('__next')
}

const mapStateToProps = (state, ownProps) => {
  if (ownProps.inReactSelect) {
    return ownProps.inReactSelect.valueArray.reduce(
      (acc, v) => ({
        selectedTags: [...acc.selectedTags, v.tag.id],
        selectedTagsNames: [...acc.selectedTagsNames, v.tag.name],
      }),
      {
        selectedTags: [],
        selectedTagsNames: [],
      },
    )
  } else if (ownProps.assignedTags) {
    return ownProps.assignedTags.reduce(
      (acc, t) => ({
        selectedTags: [...acc.selectedTags, t.id],
        selectedTagsNames: [...acc.selectedTagsNames, t.name],
      }),
      {
        selectedTags: [],
        selectedTagsNames: [],
      },
    )
  }
}

const getNodeHeight = (node: HTMLDivElement | null | undefined): number => {
  if (node) {
    const { height } = node.getBoundingClientRect()
    return height
  }

  return 0
}

const tagToStr = (tag: NativeTagWithCategory) =>
  `${tag.name} ${tag.category.name}`

type Props = {
  inReactSelect?: {
    inputValue: string
    valueArray: {
      tag: NativeTagWithCategory
    }[]
    selectValue: (arg0: { value: number }) => void
    removeValue: (arg0: { value: number }) => void
  }
  assignedTags?: NativeTagWithCategory[]
  selectedTags: number[]
  selectedTagsNames: string[]
  trackIds?: number[]
  onClose?: () => void
}
const MENU_ITEM_HEIGHT = 32
const RECENT_SEARCHES_KEY = 'ntRecent'

/**
 * Native tags popover component for selecting and managing track tags
 * Complex component with search, categorization, recent tags, and tag creation
 * Supports both React Select integration and standalone modal usage
 * Handles keyboard navigation, tag selection/deselection, and persistence
 * @param inReactSelect - Integration object for React Select dropdown usage
 * @param assignedTags - Currently assigned tags for the tracks
 * @param selectedTags - Array of selected tag IDs
 * @param selectedTagsNames - Mapping of tag IDs to tag names
 * @param trackIds - Array of track IDs to apply tags to
 * @param onClose - Callback when popover is closed
 */
const NativeTagsPopover = (props: Props) => {
  const dispatch = useDispatch()
  let [recentTags, setRecentTags] = useLocalStorage<number[]>(
    RECENT_SEARCHES_KEY,
    [],
  )
  // safeguard for invalid `recentTags`
  if (!Array.isArray(recentTags)) recentTags = []
  const nativeCategories: NativeCategory[] = useSelector(getNativeCategories)
  const nativeTags: NativeTagWithCategory[] = useSelector(getNativeTags)
  const isNativeTagsReadonly: boolean = useSelector(
    (state: State) =>
      state.user.business?.private_metadata.is_native_tags_readonly,
  )
  const colorIndicatorsEnabled = useFeatureEnabled(COLOR_INDICATORS)
  const [query, setQuery] = useState<string>('')
  const updateSearchQuery = useCallback((value: string) => {
    setQuery(value)
    setFocusIndex(0)
  }, [])
  const [focusIndex, setFocusIndex] = useState(0)
  const [selectedTagsIds, setSelectedTagsIds] = useState<number[]>([])
  const [prevSelectedTags, setPrevSelectedTags] = useState<number[]>([])

  const getNativeTagsFromIds = (tagIds: number[]): NativeTagWithCategory[] =>
    tagIds
      .map(id => nativeTags.find(tag => tag.id === id))
      .filter((tag): tag is NativeTagWithCategory => Boolean(tag))

  useEffect(() => {
    if (
      JSON.stringify(props.selectedTags) !== JSON.stringify(prevSelectedTags)
    ) {
      setPrevSelectedTags(props.selectedTags)
      setSelectedTagsIds(props.selectedTags)
    }
  }, [props.selectedTags])
  const tags =
    query === ''
      ? nativeTags
      : orderBy(
          nativeTags.filter(tag => matchTag(tagToStr(tag), query)),
          tag => tagScore(tagToStr(tag), query),
          'desc',
        )
  const actualRecentTags: NativeTagWithCategory[] =
    getNativeTagsFromIds(recentTags)
  const containerRef = useRef(null)
  const headerRef = useRef(null)
  const [isHeaderSticky, setIsHeaderSticky] = useState<boolean>(true)
  const handleResize = useCallback(() => {
    if (containerRef.current && headerRef.current) {
      const containerHeight = getNodeHeight(containerRef.current)
      const headerHeight = getNodeHeight(headerRef.current)

      if (containerHeight - headerHeight > MENU_ITEM_HEIGHT * 5) {
        setIsHeaderSticky(true)
      } else {
        setIsHeaderSticky(false)
      }
    }
  }, [containerRef, headerRef])
  useEventListener('resize', debounce(handleResize, 300))
  useLayoutEffect(handleResize, [selectedTagsIds])
  const visibleTags: NativeTagWithCategory[] =
    query === '' ? actualRecentTags : tags
  useEffect(() => {
    if (props.inReactSelect) {
      updateSearchQuery(props.inReactSelect.inputValue)
    }
  }, [props.inReactSelect?.inputValue])

  const getHeight = () => {
    const itemHeight = 36
    const titleHeight = 31
    const num = actualRecentTags.length
    const baseHeight = props.inReactSelect ? 302 : 403
    return baseHeight + num * itemHeight + (num > 1 ? titleHeight : 0)
  }

  const _onHover = index => setFocusIndex(index)

  const _handleSelect = (tag: NativeTag) => {
    if (props.inReactSelect) {
      props.inReactSelect.selectValue({
        value: tag.id,
      })
      mixpanel.sendPureEvent('Advanced search – track tags', {
        Tags: [...props.selectedTagsNames, tag.name],
      })
      return
    }

    if (props.trackIds) {
      setSelectedTagsIds([...selectedTagsIds, tag.id])
    }

    if (!recentTags.includes(tag.id)) {
      setRecentTags(uniq([tag.id].concat(recentTags)).slice(0, 3))
    }
  }

  const _handleUnselect = (tag: NativeTag) => {
    if (props.inReactSelect) {
      props.inReactSelect.removeValue({
        value: tag.id,
      })
      return
    }

    if (props.trackIds) {
      setSelectedTagsIds(selectedTagsIds.filter(id => id !== tag.id))
    }
  }

  const handleKeyUp = (event: React.KeyboardEvent<HTMLElement>) => {
    const selectKey = keyEqualTo(event, 'Enter')

    if (selectKey && visibleTags.length > focusIndex) {
      // TODO: fix keyboard selection to be working on all submenus
      // TODO: remove 'Tab' as hotkey from normal playlist tags
      const tag = visibleTags[focusIndex]

      if (tag && !selectedTagsIds.includes(tag.id)) {
        if (query) {
          updateSearchQuery('')
        }

        _handleSelect(tag)
      }
    }
  }

  const [newTag, setNewTag] = useState<Partial<NativeTag> | null | undefined>(
    null,
  )
  const [saving, setSaving] = useState<boolean>(false)

  const createTag = async () => {
    setSaving(true)

    if (newTag && newTag.name != null && newTag.category_id != null) {
      try {
        const tag = await dispatch(
          createCustomNativeTag(newTag.name, newTag.category_id),
        )
        setSelectedTagsIds([...selectedTagsIds, tag.id])
        setNewTag(null)
      } catch (error) {}
    }

    setSaving(false)
    updateSearchQuery('')
  }

  const renderTagList = (tags: NativeTagWithCategory[]) =>
    query && tags.length === 0 ? (
      <div
        className="NativeTagsPopover_Item"
        onClick={() => {
          setNewTag({
            name: query.trim(),
          })
        }}
      >
        <TextOverflow content={`${query} (create new tag)`} />
      </div>
    ) : (
      tags.map((tag, index) => {
        const itemClass = cn('NativeTagsPopover_Item', {
          'NativeTagsPopover_Item--focused': index === focusIndex,
        })
        return (
          <div
            className={itemClass}
            key={index}
            onMouseOver={() => _onHover(index)}
            onClick={() => toggleTag(tag)}
          >
            {colorIndicatorsEnabled && tag.color && (
              <span
                className="NativeTagsPopover_Item_Color"
                style={{
                  background: COLORS[tag.color],
                }}
              />
            )}
            <TextOverflow content={tag.name} />
            <div className="NativeTagsPopover_Category">
              {tag.category.name}
            </div>
            <div className="NativeTagsPopover_Check">
              {selectedTagsIds.includes(tag.id) && (
                <Icon type="check" width="16" height="16" />
              )}
            </div>
          </div>
        )
      })
    )

  const toggleTag = (tag: NativeTag) => {
    if (query) {
      updateSearchQuery('')
    }

    if (selectedTagsIds.includes(tag.id)) {
      _handleUnselect(tag)
    } else {
      _handleSelect(tag)
    }
  }

  const handleSubmit = async () => {
    const addedIds = selectedTagsIds.filter(
      id => !props.selectedTags.includes(id),
    )
    const removedIds = props.selectedTags.filter(
      id => !selectedTagsIds.includes(id),
    )

    if (addedIds.length) {
      const tags = nativeTags
        .filter(tag => addedIds.includes(tag.id))
        .map(tag => tag.name)

      if (props.trackIds && props.trackIds.length > 1) {
        mixpanel.sendPureEvent('Add multiple native tags', {
          Tags: tags,
        })
      } else {
        mixpanel.sendPureEvent('Add native tag', {
          Tags: tags,
        })
      }
    }

    setSaving(true)

    try {
      if (props.trackIds) {
        await dispatch(
          applyNativeTagsToTracks(props.trackIds, addedIds, removedIds),
        )
      }
    } catch (_) {
      return setSaving(false)
    }

    setSaving(false)

    if (props.onClose) {
      props.onClose()
    }
  }

  const tagsInput = useMemo(
    () =>
      getNativeTagsFromIds(selectedTagsIds).map(
        (tag: NativeTagWithCategory) => ({
          color: colorIndicatorsEnabled ? tag.color : null,
          name: tag.name,
          value: tag.id,
        }),
      ),
    [selectedTagsIds],
  )
  const menuItemRefs = useRef(
    nativeCategories.map<React.RefObject<HTMLDivElement>>(React.createRef),
  )
  const [activeMenu, setActiveMenu] = useState<number | null>(null)
  const [menuItemPosition, setMenuItemPosition] = useState<
    DOMRect | null | undefined
  >(null)
  const tagMenu = (
    <div
      className={cn('NativeTagsPopover', {
        NativeTagsPopover__inReactSelect: props.inReactSelect,
      })}
      style={{
        minHeight: props.inReactSelect ? getHeight() : undefined,
      }}
      onKeyUp={handleKeyUp}
    >
      <div className="NativeTagsPopover_Scroll" ref={containerRef}>
        <div className="NativeTagsPopover_Scroll_Content">
          {!props.inReactSelect && (
            <div
              className={cn('NativeTagsPopover_Header', {
                NativeTagsPopover_Header__Sticky: isHeaderSticky,
              })}
              ref={headerRef}
            >
              <Box mb="4">
                <Heading size={600}>Tag tracks</Heading>
                {!isNativeTagsReadonly && <ColorIndicatorsAlert />}
              </Box>
              <TagsInput
                autoFocus
                value={query}
                tags={tagsInput}
                onChange={setSelectedTagsIds}
                onInputChange={updateSearchQuery}
              />
            </div>
          )}
          <div className="NativeTagsPopover_Menu">
            <div className="NativeTagsPopover_Section">
              {query === '' && visibleTags.length > 0 && (
                <div className="NativeTagsPopover_Title">Recent tags</div>
              )}
              {renderTagList(visibleTags)}
            </div>
            {query === '' && (
              <div className="NativeTagsPopover_Section">
                <div className="NativeTagsPopover_Title">
                  Select by category
                </div>
                {nativeCategories.map(
                  (category: NativeCategory, index: number) => {
                    const ref = menuItemRefs.current[index]

                    const renderPopover = () => (
                      <Popover
                        compact
                        items={category.native_tags.map((tag: NativeTag) => ({
                          nodeRight: selectedTagsIds.includes(tag.id) ? (
                            <Icon type="check" width="16" height="16" />
                          ) : undefined,
                          label: tag.name,
                          renderIcon:
                            colorIndicatorsEnabled && tag.color
                              ? () => (
                                  <span
                                    className="NativeTagsPopover_Item_Color"
                                    style={{
                                      background: tag.color
                                        ? COLORS[tag.color]
                                        : undefined,
                                    }}
                                  />
                                )
                              : undefined,
                          onClick: () => toggleTag(tag),
                        }))}
                      />
                    )

                    const renderSubmenu = () =>
                      props.inReactSelect ? (
                        <div
                          className="NativeTagsPopover_Submenu"
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            left: '100%',
                          }}
                        >
                          {renderPopover()}
                        </div>
                      ) : (
                        appNode &&
                        menuItemPosition &&
                        ReactDOM.createPortal(
                          <div
                            className="NativeTagsPopover_Submenu"
                            style={{
                              position: 'fixed',
                              top: menuItemPosition.bottom + getPageYOffset(),
                              left:
                                menuItemPosition.left +
                                menuItemPosition.width +
                                getPageXOffset(),
                              zIndex: 9999,
                            }}
                          >
                            {renderPopover()}
                          </div>,
                          appNode,
                        )
                      )

                    return (
                      <div
                        className="NativeTagsPopover_Item"
                        key={category.id}
                        ref={ref}
                        onMouseEnter={() => {
                          setActiveMenu(category.id)
                          setMenuItemPosition(
                            ref &&
                              ref.current &&
                              ref.current.getBoundingClientRect(),
                          )
                        }}
                        onMouseLeave={() => {
                          setActiveMenu(null)
                          setMenuItemPosition(null)
                        }}
                      >
                        <TextOverflow content={category.name} />
                        <div className="NativeTagsPopover_ItemIcon">
                          <Icon type="arrow-right" />
                        </div>
                        {activeMenu === category.id && renderSubmenu()}
                      </div>
                    )
                  },
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {!props.inReactSelect && (
        <div className="NativeTagsPopover_Footer">
          <div>
            <Button
              appearance="inline-link"
              caption="Manage tags"
              link="/settings/track-tags"
            />
          </div>
          <div>
            <Button
              appearance="primary"
              caption="Save"
              disabled={isEqual(props.selectedTags, selectedTagsIds)}
              onClick={handleSubmit}
              progressCaption="Loading"
              progressIndicator={saving}
            />
          </div>
        </div>
      )}
    </div>
  )
  if (props.inReactSelect) return tagMenu
  return (
    <DiscoModal onRequestClose={props.onClose || (() => {})}>
      <div className="modal-width--400">
        {newTag ? (
          <div className="NativeTagsPopover">
            <div className="NativeTagsPopover_Scroll">
              <div className="NativeTagsPopover_Scroll_Content">
                <div className="NativeTagsPopover_Header">
                  <Heading size={600}>
                    Select category for “{newTag.name}”
                  </Heading>
                  <Tag tag={newTag.name ?? ''} />
                </div>
                <div className="NativeTagsPopover_Menu">
                  {nativeCategories.map((category: NativeCategory) => (
                    <div
                      className="NativeTagsPopover_Item"
                      key={category.id}
                      onClick={() =>
                        setNewTag({ ...newTag, category_id: category.id })
                      }
                    >
                      <TextOverflow content={category.name} />
                      <div className="NativeTagsPopover_ItemIcon">
                        <RadioButton
                          checked={category.id === newTag.category_id}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="NativeTagsPopover_Footer">
              <div>
                <Button
                  appearance="inline-link"
                  caption="Cancel"
                  onClick={() => setNewTag(null)}
                />
              </div>
              <div>
                <Button
                  appearance="primary"
                  caption="Create tag"
                  disabled={!newTag.category_id}
                  progressCaption="Creating"
                  progressIndicator={saving}
                  onClick={createTag}
                />
              </div>
            </div>
          </div>
        ) : (
          tagMenu
        )}
      </div>
    </DiscoModal>
  )
}

export default connect(mapStateToProps)(NativeTagsPopover) as any
